/* eslint-disable simple-import-sort/imports*/

import '@fontsource/noto-sans'
import '@fontsource/noto-sans-sc'
import 'main.css'

import {
  loadConfig,
  LogoutManager,
  patchConsoleError,
  useIDTagging,
  useTenantComponentLibrarySetting,
} from '@msdk/common'
import { configureModelsMetadataCompressionWorkerFactory } from '@msdk/definitions'
import * as FunctionalComponents from '@msdk/functional-components'
import { ComponentLibraryProvider, ThemeProvider, validateUILicense } from '@msdk/ui'
import { FullScreenLoader } from 'components/FullScreenLoader'
import AboutPage from 'pages/About'
import { ClinicSwitch } from 'pages/ClinicSwitch'
import Custom from 'pages/Custom'
import Form from 'pages/Form'
import { Home } from 'pages/Home'
import { Legacy } from 'pages/Legacy'
import { NoTenantHome } from 'pages/NoTenantHome'
import View from 'pages/View'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from 'react-router-dom'
import { RecoilRoot } from 'recoil'
import { initializeTelemetry } from 'telemetry/initialize'
import { attachCompressionToWindow } from 'utils'
import App from './pages/App'

import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'

const cache = createCache({
  key: 'msdk',
  prepend: true,
  container: document.body,
})

patchConsoleError()

loadConfig()
configureModelsMetadataCompressionWorkerFactory(
  () =>
    new Worker(new URL('./workers/metadata-storage.worker.ts', import.meta.url), {
      type: 'module',
    }),
)

validateUILicense(window.__APP_CONFIG__.MUI_LICENSE_KEY)
/**
 * Storing functional components in a global variable, these
 * will be used during component resolution.
 * This is here because component resolution happens inside other libraries and having a
 * reference to `FunctionalComponents` inside them would casue a cyrcular dependency
 * -
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const windowObj = window as any
windowObj.FunctionalComponents = FunctionalComponents
attachCompressionToWindow()
// Init the logout manager, this has to be a single instance for the entire application
// that is why it is being save in the window, probably more elegant soultions could be used
// but since it is critical that this is a single instance created only once we use the window,
// this way whe are sure any React shenanigans will not interfere with this instance.
windowObj.LogoutManager = new LogoutManager()

function RouterWrapper() {
  const { componentLibrary } = useTenantComponentLibrarySetting()
  const idTaggingEnabled = useIDTagging()

  return (
    <>
      <ComponentLibraryProvider library={componentLibrary}>
        <ThemeProvider idTaggingEnabled={idTaggingEnabled}>
          <RouterProvider router={router} fallbackElement={<FullScreenLoader />} />
        </ThemeProvider>
      </ComponentLibraryProvider>
    </>
  )
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<App />}>
      <Route path="" element={<NoTenantHome />} />
      <Route path=":tenant" element={<Home />} />
      <Route path=":tenant/:sitemap/switch-to-clinic/:id" element={<ClinicSwitch />} />
      <Route path=":tenant/:sitemap/CUSTOM/:moduleName/*" element={<Custom />} />
      <Route path=":tenant/:sitemap/:entityName/:moduleName" element={<View />} handle={{ crumb: true }} />
      <Route path=":tenant/:sitemap/:sitemapId" element={<View />} handle={{ crumb: true }} />
      <Route path=":tenant/:sitemap/:entityName/:moduleName/:id" element={<Form />} handle={{ crumb: true }} />
      <Route path=":tenant/BU/:buId/:sitemap/:contextId/*" element={<Legacy />} />
      <Route path=":tenant/:sitemap/About" element={<AboutPage />} />
    </Route>,
  ),
)

root.render(
  <React.StrictMode>
    <CacheProvider value={cache}>
      <RecoilRoot>
        <RouterWrapper></RouterWrapper>
      </RecoilRoot>
    </CacheProvider>
  </React.StrictMode>,
)

if ((window.__APP_CONFIG__.TELEMETRY_ENABLED as string) === 'true') {
  initializeTelemetry()
}

// release
